import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import moment from "moment";
import { req } from "../../utils";

export const WarehouseServicingStats = ({ dateRanges }) => {
  const [warehouseStats, setWarehouseStats] = useState([]);
  const [summary, setSummary] = useState(null);

  const startTime = useMemo(() => dateRanges && dateRanges[0] ? moment(dateRanges[0]) : null, [dateRanges]);
  const endTime = useMemo(() => dateRanges && dateRanges[1] ? moment(dateRanges[1]) : null, [dateRanges]);

  const formatNumber = (number) => {
    try {
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } catch (e) {
      return number;
    }
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Build the endpoint with date parameters
  const endpoint = useMemo(() => {
    if (!dateRanges || dateRanges.length !== 2 || !startTime || !endTime) return null;
    
    const startUnix = startTime.unix();
    const endUnix = endTime.unix();
    const url = `get_warehouse_servicing_stats?s=${startUnix}&e=${endUnix}`;
    console.log('Warehouse endpoint:', url);
    return url;
  }, [dateRanges, startTime, endTime]);

  // Fetch data directly
  const fetchData = useCallback(async () => {
    if (!endpoint) return;

    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching warehouse data from:', endpoint);
      const data = await req('GET', endpoint);
                    console.log('Warehouse data received:', data);
       
       if (data && data.warehouse_stats) {
         setWarehouseStats(data.warehouse_stats);
         setSummary(data.summary);
       }
    } catch (err) {
      console.error('Warehouse API error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!dateRanges || dateRanges.length !== 2 || !startTime || !endTime) {
    return null;
  }

  if (loading) {
    return (
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12}>
          <div>Loading warehouse servicing stats...</div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12}>
          <div>Error loading warehouse servicing stats: {error.message || error}</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                    {/* Summary Stats Card */}
       {summary ? (
         <Grid item xs={12} md={6}>
           <div className="card card-stat-summary animate-bg-primary">
             <div className="card-body">
               <div className="card-stat-summary--title">Warehouse Servicing Summary</div>
               <div className="card-stat-summary--total-wrapper">
                 <div style={{ marginRight: "2rem" }}>
                   <div className="card-stat-summary--total">
                     {formatNumber(summary.total_machines_in_period)}
                     <div className="card-stat-summary--total-subtitle">Machines In</div>
                   </div>
                 </div>
                 <div style={{ marginRight: "2rem" }}>
                   <div className="card-stat-summary--total">
                     {formatNumber(summary.total_machines_out_period)}
                     <div className="card-stat-summary--total-subtitle">Machines Out</div>
                   </div>
                 </div>
                 <div>
                   <div className="card-stat-summary--total">
                     {formatNumber(summary.current_servicing_total)}
                     <div className="card-stat-summary--total-subtitle">Currently Servicing</div>
                   </div>
                 </div>
               </div>
                             <div className="card-stat-summary--meta">
                 <div className="card-stat-summary--meta-timeline">
                   {startTime && endTime ? `${startTime.format('MMM DD, YYYY')} - ${endTime.format('MMM DD, YYYY')}` : 'Date range not available'}
                 </div>
               </div>
             </div>
           </div>
         </Grid>
       ) : (
         <Grid item xs={12} md={6}>
           <div className="card card-stat-summary animate-bg-primary">
             <div className="card-body">
               <div className="card-stat-summary--title">Warehouse Servicing Summary</div>
               <div className="card-stat-summary--total-wrapper">
                 <div>No data available</div>
               </div>
               <div className="card-stat-summary--meta">
                 <div className="card-stat-summary--meta-timeline">
                   {startTime && endTime ? `${startTime.format('MMM DD, YYYY')} - ${endTime.format('MMM DD, YYYY')}` : 'Date range not available'}
                 </div>
               </div>
             </div>
           </div>
         </Grid>
       )}

                    {/* Detailed Warehouse Stats Table */}
       {warehouseStats.length > 0 ? (
         <Grid item xs={12} md={6}>
           <div className="card card-stat-summary animate-bg-primary">
             <div className="card-body">
               <div className="card-stat-summary--title">Warehouse Servicing Details</div>
               <TableContainer>
                 <Table size="medium">
                   <TableHead>
                     <TableRow>
                       <TableCell><strong>Warehouse</strong></TableCell>
                       <TableCell><strong>State</strong></TableCell>
                       <TableCell align="right"><strong>Machines In</strong></TableCell>
                       <TableCell align="right"><strong>Machines Out</strong></TableCell>
                       <TableCell align="right"><strong>Currently Servicing</strong></TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {warehouseStats.map((warehouse, index) => (
                       <TableRow 
                         key={index}
                         sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                       >
                         <TableCell>{warehouse.warehouse_name}</TableCell>
                         <TableCell>{warehouse.state}</TableCell>
                         <TableCell align="right">{formatNumber(warehouse.machines_in)}</TableCell>
                         <TableCell align="right">{formatNumber(warehouse.machines_out)}</TableCell>
                         <TableCell align="right">{formatNumber(warehouse.current_servicing)}</TableCell>
                       </TableRow>
                     ))}
                   </TableBody>
                 </Table>
               </TableContainer>
               <div className="card-stat-summary--meta">
                 <div className="card-stat-summary--meta-timeline">
                   {startTime && endTime ? `${startTime.format('MMM DD, YYYY')} - ${endTime.format('MMM DD, YYYY')}` : 'Date range not available'}
                 </div>
               </div>
             </div>
           </div>
         </Grid>
       ) : (
         <Grid item xs={12} md={6}>
           <div className="card card-stat-summary animate-bg-primary">
             <div className="card-body">
               <div className="card-stat-summary--title">Warehouse Servicing Details</div>
               <div>No warehouse data available</div>
               <div className="card-stat-summary--meta">
                 <div className="card-stat-summary--meta-timeline">
                   {startTime && endTime ? `${startTime.format('MMM DD, YYYY')} - ${endTime.format('MMM DD, YYYY')}` : 'Date range not available'}
                 </div>
               </div>
             </div>
           </div>
         </Grid>
       )}
    </Grid>
  );
};

export default WarehouseServicingStats; 